import { Group, Select, Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import type { JSX } from "react";
import { useMutation } from "urql";
import { DEFAULT_IMAGE_QUALITY, DEFAULT_IMAGE_SIZE, IMAGE_QUALITY_OPTIONS, IMAGE_SIZE_OPTIONS } from "../utils";
import PromptInput from "./PromptInput";
import {
    type ImageThreadIterateMutation,
    type ImageThreadIterateMutationVariables,
    imageThreadIterateMutation,
} from "./queries";

interface Props {
    imageThreadId: number;
    defaults: { size?: string | undefined; quality?: string | undefined };
    onCreated: VoidFunction;
}

interface FormValues {
    text: string;
    size: string;
    quality: string;
}

export default function NewIterationForm({ imageThreadId, defaults, onCreated }: Props): JSX.Element {
    const initialSize = IMAGE_SIZE_OPTIONS.includes((defaults.size ?? "") as any)
        ? (defaults.size as string)
        : DEFAULT_IMAGE_SIZE;
    const initialQuality = IMAGE_QUALITY_OPTIONS.includes((defaults.quality ?? "") as any)
        ? (defaults.quality as string)
        : DEFAULT_IMAGE_QUALITY;

    const form = useForm<FormValues>({
        initialValues: {
            text: "",
            size: initialSize,
            quality: initialQuality,
        },
        validate: {
            text: (value) => (value.trim() ? null : "Required"),
        },
    });

    const [result, executeIterate] = useMutation<ImageThreadIterateMutation, ImageThreadIterateMutationVariables>(
        imageThreadIterateMutation,
    );

    const handleSubmit = form.onSubmit(async (values) => {
        const res = await executeIterate({
            imageThreadId,
            text: values.text,
            size: values.size || undefined,
            quality: values.quality || undefined,
        });
        if (res.error) {
            notifications.show({ title: "Failed to iterate", message: res.error.message });
            return;
        }
        form.reset();
        onCreated();
    });

    return (
        <form onSubmit={handleSubmit}>
            <Stack>
                <PromptInput
                    placeholder="Describe the refinement..."
                    {...form.getInputProps("text")}
                    submitting={result.fetching}
                    onSubmit={handleSubmit}
                />
                <Group gap="sm">
                    <Select
                        placeholder="Size"
                        data={[...IMAGE_SIZE_OPTIONS]}
                        size="xs"
                        w={200}
                        clearable
                        {...form.getInputProps("size")}
                    />
                    <Select
                        placeholder="Quality"
                        data={[...IMAGE_QUALITY_OPTIONS]}
                        size="xs"
                        w={160}
                        clearable
                        {...form.getInputProps("quality")}
                    />
                </Group>
            </Stack>
        </form>
    );
}
